/**
 * AdminHelpSettings - configurações do chat de dúvidas (ligado, público, modelos e tetos).
 * Salvar envia só o que mudou; a API aplica em até 60 s (cache das configurações).
 */

import React, { useEffect, useState } from 'react';
import AdminHelpLoadError from '@/components/admin/help/AdminHelpLoadError';
import { getHelpSettings, helpErrorMessage, patchHelpSettings, type HelpSettings } from '@/services/adminHelpService';

// Os números ficam como texto no formulário para o campo poder ficar vazio enquanto se digita.
type SettingsDraft = Omit<HelpSettings, 'dailyCap' | 'monthlyCapUsd'> & { dailyCap: string; monthlyCapUsd: string };

const FIELD_CLASS = 'w-full px-3 py-1.5 text-sm rounded-md border border-border bg-background text-foreground';

const toDraft = (s: HelpSettings): SettingsDraft => ({
  ...s,
  dailyCap: String(s.dailyCap),
  monthlyCapUsd: String(s.monthlyCapUsd),
});

/** Campo numérico em branco é inválido, e não zero (Number('') daria 0). */
const parseNumber = (raw: string) => (raw.trim() === '' ? NaN : Number(raw));

type CapErrors = { dailyCap: string | null; monthlyCapUsd: string | null };

// Teto mensal 0 é aceito pela API, mas desliga o chat para todos: só um zero digitado de propósito chega lá.
function capErrors(draft: SettingsDraft): CapErrors {
  const dailyCap = parseNumber(draft.dailyCap);
  const monthlyCapUsd = parseNumber(draft.monthlyCapUsd);
  return {
    dailyCap: Number.isNaN(dailyCap)
      ? 'Informe um número'
      : Number.isInteger(dailyCap) && dailyCap > 0
        ? null
        : 'Use um número inteiro maior que 0',
    monthlyCapUsd: Number.isNaN(monthlyCapUsd)
      ? 'Informe um número'
      : Number.isFinite(monthlyCapUsd) && monthlyCapUsd >= 0
        ? null
        : 'Use um número igual ou maior que 0',
  };
}

/** Só as chaves que mudaram (os tetos já validados por capErrors). */
function changedSettings(saved: HelpSettings, draft: SettingsDraft): Partial<HelpSettings> {
  const patch: Partial<HelpSettings> = {};
  if (draft.enabled !== saved.enabled) patch.enabled = draft.enabled;
  if (draft.audience !== saved.audience) patch.audience = draft.audience;
  if (draft.model.trim() !== saved.model) patch.model = draft.model.trim();
  if (draft.fallbackModel.trim() !== saved.fallbackModel) patch.fallbackModel = draft.fallbackModel.trim();
  const dailyCap = parseNumber(draft.dailyCap);
  if (dailyCap !== saved.dailyCap) patch.dailyCap = dailyCap;
  const monthlyCapUsd = parseNumber(draft.monthlyCapUsd);
  if (monthlyCapUsd !== saved.monthlyCapUsd) patch.monthlyCapUsd = monthlyCapUsd;
  return patch;
}

const AdminHelpSettings: React.FC = () => {
  const [saved, setSaved] = useState<HelpSettings | null>(null);
  const [draft, setDraft] = useState<SettingsDraft | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  useEffect(() => {
    let stale = false;
    setLoading(true);
    setError(null);
    getHelpSettings()
      .then((data) => {
        if (stale) return;
        setSaved(data);
        setDraft(toDraft(data));
      })
      .catch((err) => {
        if (!stale) setError(helpErrorMessage(err));
      })
      .finally(() => {
        if (!stale) setLoading(false);
      });
    return () => {
      stale = true;
    };
  }, [reloadKey]);

  if (error) return <AdminHelpLoadError message={error} onRetry={() => setReloadKey((k) => k + 1)} />;
  if (loading || !saved || !draft) {
    return <div className="py-8 text-center text-muted-foreground">Carregando configurações...</div>;
  }

  const patch = changedSettings(saved, draft);
  const dirty = Object.keys(patch).length > 0;
  const errors = capErrors(draft);
  const invalid = errors.dailyCap !== null || errors.monthlyCapUsd !== null;
  const update = (changes: Partial<SettingsDraft>) => setDraft((prev) => (prev ? { ...prev, ...changes } : prev));

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!dirty || invalid || saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      const next = await patchHelpSettings(patch);
      setSaved(next);
      setDraft(toDraft(next));
      setSavedAt(new Date());
    } catch (err) {
      setSaveError(helpErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="max-w-xl space-y-5 rounded-lg border border-border p-4">
      <div>
        <h3 className="text-sm font-semibold text-foreground">Configurações do chat de dúvidas</h3>
        <p className="text-xs text-muted-foreground">Efeito em até 60 s, sem reiniciar nada.</p>
      </div>

      <label className="flex items-center gap-2 text-sm text-foreground">
        <input
          type="checkbox"
          checked={draft.enabled}
          onChange={(e) => update({ enabled: e.target.checked })}
          className="size-4 accent-primary"
        />
        Chat ligado
      </label>

      <Field label="Público">
        <select
          value={draft.audience}
          onChange={(e) => update({ audience: e.target.value === 'all' ? 'all' : 'admins' })}
          className={FIELD_CLASS}
        >
          <option value="admins">Só admins</option>
          <option value="all">Todos</option>
        </select>
      </Field>

      <Field label="Modelo">
        <input
          type="text"
          value={draft.model}
          onChange={(e) => update({ model: e.target.value })}
          maxLength={100}
          className={FIELD_CLASS}
        />
      </Field>

      <Field label="Modelo de reserva">
        <input
          type="text"
          value={draft.fallbackModel}
          onChange={(e) => update({ fallbackModel: e.target.value })}
          maxLength={100}
          className={FIELD_CLASS}
        />
      </Field>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Teto diário por pessoa" error={errors.dailyCap}>
          <input
            type="number"
            min={1}
            step={1}
            inputMode="numeric"
            value={draft.dailyCap}
            onChange={(e) => update({ dailyCap: e.target.value })}
            aria-invalid={errors.dailyCap !== null}
            className={FIELD_CLASS}
          />
        </Field>
        <Field label="Teto mensal em US$" error={errors.monthlyCapUsd}>
          <input
            type="number"
            min={0}
            step={0.5}
            inputMode="decimal"
            value={draft.monthlyCapUsd}
            onChange={(e) => update({ monthlyCapUsd: e.target.value })}
            aria-invalid={errors.monthlyCapUsd !== null}
            className={FIELD_CLASS}
          />
        </Field>
      </div>

      {saveError && <p className="break-words text-sm text-destructive">{saveError}</p>}

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="submit"
          disabled={!dirty || invalid || saving}
          className="rounded-md bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {saving ? 'Salvando...' : 'Salvar'}
        </button>
        {savedAt && !dirty && (
          <span className="text-xs text-muted-foreground">
            Salvo às {savedAt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
          </span>
        )}
      </div>
    </form>
  );
};

function Field({ label, error = null, children }: { label: string; error?: string | null; children: React.ReactNode }) {
  return (
    <label className="block space-y-1">
      <span className="block text-xs font-medium text-muted-foreground">{label}</span>
      {children}
      {error && <span className="block text-xs text-destructive">{error}</span>}
    </label>
  );
}

export default AdminHelpSettings;
